import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import UpdateToast from './components/UpdateToast';
import PageSkeleton from './components/PageSkeleton';
import InstallAppPrompt from './components/InstallAppPrompt';
import { SensorProvider } from './context/SensorContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppErrorBoundary from './components/AppErrorBoundary';
import { setClientErrorContext } from './utils/clientErrorReporter';
import { ImpersonationProvider } from './context/ImpersonationContext';
import { SystemSettingsProvider, useSystemSettings } from './context/SystemSettingsContext';
import { TenantProvider, useTenant } from './context/TenantContext';
import { TenantThemeProvider } from './theme/TenantThemeProvider';
import { LocationProvider, useLocation as useLocationContext } from './contexts/LocationContext';
import { SupportProvider } from './context/SupportContext';
import Navigation from './components/Navigation';
import BillingRecoveryBanner from './components/BillingRecoveryBanner';
import SupportNotificationBanner from './components/SupportNotificationBanner';
import SupportModeBanner from './components/SupportModeBanner';
import ViewAsBanner from './components/ViewAsBanner';
import { isViewingAs } from './utils/viewAs';
import AdminRoute from './components/AdminRoute';
import ManagerRoute from './components/ManagerRoute';
import DeveloperRoute from './components/DeveloperRoute';
import useUserActivityTracker from './hooks/useUserActivityTracker';
import { companyPageAllowed } from './utils/companyAccess';

// Static imports — critical path pages that most users hit immediately
import LoginPage from './pages/LoginPage';
import ChemicalUsagePage from './pages/ChemicalUsagePage';

// A chunk that fails to load is usually a cache problem: a shell cached across
// a deploy naming chunks that no longer exist, or (2026-09-22) a service worker
// that had stored the SPA-rewrite HTML under a chunk URL. Drop every cache and
// worker this origin holds, then reload once. The guard is cleared only after
// a chunk actually loads; clearing it on App mount turned "reload once" into
// "reload forever" whenever the bad response was cached.
async function purgeClientCaches() {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(key => caches.delete(key)));
    }
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(registration => registration.unregister()));
    }
  } catch (error) {
    console.warn('[chunk-retry] Cache purge failed:', error);
  }
}

function lazyWithRetry(importFn) {
  return React.lazy(() =>
    importFn()
      .then(module => {
        sessionStorage.removeItem('chunk_reload');
        return module;
      })
      .catch(async (error) => {
        if (!sessionStorage.getItem('chunk_reload')) {
          sessionStorage.setItem('chunk_reload', '1');
          await purgeClientCaches();
          window.location.reload();
          return new Promise(() => {}); // never resolves — page is reloading
        }
        // Already reloaded once for this session: surface the real error
        // instead of reloading again.
        throw error;
      })
  );
}

// Lazy-loaded pages — downloaded on-demand when user navigates to the route
// Public / auth pages
const SignupPage = lazyWithRetry(() => import('./pages/SignupPage'));
const PublicSignupPage = lazyWithRetry(() => import('./pages/PublicSignupPage'));
const PrivacyPolicyPage = lazyWithRetry(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazyWithRetry(() => import('./pages/TermsOfServicePage'));
const SupportOrgPickerPage = lazyWithRetry(() => import('./pages/SupportOrgPickerPage'));
const DemoRequestPage = lazyWithRetry(() => import('./pages/DemoRequestPage'));
const DemoVideosPage = lazyWithRetry(() => import('./pages/DemoVideosPage'));

// Core user pages
const TenantLandingPage = lazyWithRetry(() => import('./pages/TenantLandingPage'));
const TabletViewPage = lazyWithRetry(() => import('./pages/TabletViewPage'));
const BarrelInventoryPage = lazyWithRetry(() => import('./pages/BarrelInventoryPage'));
const FacilityInventoryPage = lazyWithRetry(() => import('./pages/FacilityInventoryPage'));
const HelpVideosPage = lazyWithRetry(() => import('./pages/HelpVideosPage'));
const SupportRequestsPage = lazyWithRetry(() => import('./pages/SupportRequestsPage'));

// Manager pages
const StatisticsPage = lazyWithRetry(() => import('./pages/StatisticsPage'));
const GraphsPage = lazyWithRetry(() => import('./pages/GraphsPage'));
const WashPackageHistoryPage = lazyWithRetry(() => import('./pages/WashPackageHistoryPage'));
const WashPackageCostPage = lazyWithRetry(() => import('./pages/WashPackageCostPage'));
const WashCountPage = lazyWithRetry(() => import('./pages/WashCountPage'));
const CostPerWashPage = lazyWithRetry(() => import('./pages/CostPerWashPage'));

// Admin pages
const OrderChemPage = lazyWithRetry(() => import('./pages/OrderChemPage'));
const AdminPage = lazyWithRetry(() => import('./pages/admin/AdminPage'));
const AuditLogPage = lazyWithRetry(() => import('./pages/admin/AuditLogPage'));
const AlertManagementPage = lazyWithRetry(() => import('./pages/admin/AlertManagementPage'));
const SystemSettingsPage = lazyWithRetry(() => import('./pages/admin/SystemSettingsPage'));
const BillingPage = lazyWithRetry(() => import('./pages/admin/BillingPage'));
const IntegrationsPage = lazyWithRetry(() => import('./pages/admin/IntegrationsPage'));
const OwnerSharingPage = lazyWithRetry(() => import('./pages/OwnerSharingPage'));
const CatalogManagementPage = lazyWithRetry(() => import('./pages/admin/CatalogManagementPage'));
const SdsLibraryPage = lazyWithRetry(() => import('./pages/developer/SdsLibrary'));
const InventoryConfigPage = lazyWithRetry(() => import('./pages/admin/InventoryConfigPage'));
const AddHardwarePage = lazyWithRetry(() => import('./pages/admin/AddHardwarePage'));
const AddLocationPage = lazyWithRetry(() => import('./pages/admin/AddLocationPage'));
const InjectorTipsPage = lazyWithRetry(() => import('./pages/admin/InjectorTipsPage'));
const ReferenceLibraryPage = lazyWithRetry(() => import('./pages/admin/ReferenceLibraryPage'));
const DailyRevenuePage = lazyWithRetry(() => import('./pages/DailyRevenuePage'));
const ActivityReportPage = lazyWithRetry(() => import('./pages/admin/ActivityReportPage'));
const ChecklistsPage = lazyWithRetry(() => import('./pages/admin/ChecklistsPage'));
const EnvSensorsPage = lazyWithRetry(() => import('./pages/EnvSensorsPage'));
const DataCleanupPage = lazyWithRetry(() => import('./pages/admin/DataCleanupPage'));
const WashPackageCountsPage = lazyWithRetry(() => import('./pages/admin/WashPackageCountsPage'));
const AverageVolumePage = lazyWithRetry(() => import('./pages/admin/AverageVolumePage'));
const RawPulsesPage = lazyWithRetry(() => import('./pages/admin/RawPulsesPage'));
const WashReplayPage = lazyWithRetry(() => import('./pages/admin/WashReplayPage'));
const EstimatedDilutionPage = lazyWithRetry(() => import('./pages/admin/EstimatedDilutionPage'));
const SelfServiceSetupWizard = lazyWithRetry(() => import('./pages/setup/SelfServiceSetupWizard'));

// Developer pages
const DeveloperConsole = lazyWithRetry(() => import('./pages/developer/DeveloperConsole'));
const UserManagement = lazyWithRetry(() => import('./pages/developer/UserManagement'));
const AccessMatrix = lazyWithRetry(() => import('./pages/developer/AccessMatrix'));
const FirmwareManagement = lazyWithRetry(() => import('./pages/developer/FirmwareManagement'));
const DencarSubscribers = lazyWithRetry(() => import('./pages/developer/DencarSubscribers'));

// Protected Route Component
function ProtectedRoute({ children }) {
  const { currentUser, needsOrgSelection, isSupportAccount, supportOrgs } = useAuth();

  // Not logged in - redirect to login
  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // Support user with no orgs granted - show the picker page (which displays no access message)
  if (isSupportAccount && (!supportOrgs || supportOrgs.length === 0)) {
    return <Navigate to="/support-org-picker" />;
  }

  // Support user needs to select an org (has multiple orgs, hasn't picked yet)
  if (needsOrgSelection) {
    return <Navigate to="/support-org-picker" />;
  }

  return children;
}

// Smart redirect component for logged-in users
function HomeRedirect() {
  const { hasFeatureAccess, organization, needsOrgSelection } = useAuth();
  const { navigation, tenant } = useTenant();

  // AC1: a support login has no account until it picks one - go to the picker instead of waiting for one
  if (needsOrgSelection) {
    return <Navigate to="/support-org-picker" replace />;
  }

  // Wait for organization data to load
  if (!organization) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
  }

  // Wait for tenant data to load if org has a tenantId but tenant hasn't loaded yet
  if (organization.tenantId && !tenant) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
  }

  // Dencar-only customers go straight to Integrations
  if (hasFeatureAccess('dencar') && !hasFeatureAccess('chemicalMonitoring')) {
    return <Navigate to="/admin/integrations" replace />;
  }

  // Use tenant's default page if configured, otherwise dashboard
  const defaultPage = navigation?.defaultPage || '/dashboard';
  return <Navigate to={defaultPage} replace />;
}

// Dashboard component that redirects Dencar-only users
function DashboardOrRedirect() {
  const { hasFeatureAccess, organization } = useAuth();

  // Wait for organization data to load
  if (!organization) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
  }

  // Dencar-only customers should use Integrations page instead
  if (hasFeatureAccess('dencar') && !hasFeatureAccess('chemicalMonitoring')) {
    return <Navigate to="/admin/integrations" replace />;
  }

  // Show dashboard for everyone else
  return <ChemicalUsagePage />;
}

// Sync selected location to SystemSettingsContext for location-specific timezone
function LocationSettingsSync({ children }) {
  const { selectedLocationId } = useLocationContext();
  const { setCurrentLocationId } = useSystemSettings();

  useEffect(() => {
    if (selectedLocationId) {
      setCurrentLocationId(selectedLocationId);
    }
  }, [selectedLocationId, setCurrentLocationId]);

  return children;
}

// Main App Content (separated to use auth context)
function AppContent() {
  const { currentUser, companyView, userData } = useAuth();
  const location = useLocation();
  // Browser error reports carry the account and role (no email): utils/clientErrorReporter.js
  useEffect(() => {
    setClientErrorContext({ orgId: userData?.orgId || null, role: userData?.role || null });
  }, [userData?.orgId, userData?.role]);
  // AC1: no activity heartbeat for Car Wash Minder support logins (their visits are recorded as support visits)
  // Developer View as: none either (read-only; the session start/end is logged instead)
  useUserActivityTracker(companyView || userData?.isSupportAccount || userData?.role === 'support' || isViewingAs() ? null : currentUser, location.pathname);

  // Public routes that should not show navigation
  const publicRoutes = ['/', '/login', '/register', '/privacy', '/terms', '/demo', '/demo-videos'];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  if (!companyPageAllowed(companyView, userData, location.pathname)) return <Navigate to="/dashboard" replace />;

  return (
    <div className="App">
      {currentUser && !isPublicRoute && <ViewAsBanner />}
      {currentUser && !isPublicRoute && <Navigation />}
      {currentUser && !companyView && !isPublicRoute && <BillingRecoveryBanner />}
      {currentUser && !isPublicRoute && <SupportModeBanner />}
      {currentUser && !isPublicRoute && <SupportNotificationBanner />}
      <div className="main-content">
        <AppErrorBoundary key={location.pathname}>
        <Suspense fallback={<PageSkeleton variant="table" />}>
        <Routes>
          <Route path="/" element={
            currentUser ? <HomeRedirect /> : <Navigate to="/login" />
          } />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<PublicSignupPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/demo" element={<DemoRequestPage />} />
          <Route path="/demo-videos" element={<DemoVideosPage />} />
          <Route path="/support-org-picker" element={
            currentUser ? <SupportOrgPickerPage /> : <Navigate to="/login" />
          } />
          <Route path="/signup" element={
            <ProtectedRoute>
              <SignupPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardOrRedirect />
            </ProtectedRoute>
          } />
          <Route path="/home" element={
            <ProtectedRoute>
              <TenantLandingPage />
            </ProtectedRoute>
          } />
          <Route path="/statistics" element={
            <ManagerRoute>
              <StatisticsPage />
            </ManagerRoute>
          } />
          <Route path="/chem-usage" element={
            <ManagerRoute>
              <OrderChemPage />
            </ManagerRoute>
          } />
          <Route path="/tablet-view" element={
            <ProtectedRoute>
              <TabletViewPage />
            </ProtectedRoute>
          } />
          <Route path="/graphs" element={
            <ManagerRoute>
              <GraphsPage />
            </ManagerRoute>
          } />
          <Route path="/barrel-inventory" element={
            <ProtectedRoute>
              <BarrelInventoryPage />
            </ProtectedRoute>
          } />
          <Route path="/facility-inventory" element={
            <ProtectedRoute>
              <FacilityInventoryPage />
            </ProtectedRoute>
          } />
          {/* Open to every role: the site Activity Log (what happened at the wash). */}
          <Route path="/audit-log" element={
            <ProtectedRoute>
              <AuditLogPage />
            </ProtectedRoute>
          } />
          <Route path="/wash-packages" element={
            <ManagerRoute>
              <WashPackageHistoryPage />
            </ManagerRoute>
          } />
          <Route path="/injector-usage" element={<Navigate to="/wash-packages" replace />} />
          <Route path="/wash-packages-cost" element={
            <ManagerRoute>
              <WashPackageCostPage />
            </ManagerRoute>
          } />
          <Route path="/wash-count" element={
            <ManagerRoute>
              <WashCountPage />
            </ManagerRoute>
          } />
          <Route path="/cost-per-wash" element={
            <ManagerRoute>
              <CostPerWashPage />
            </ManagerRoute>
          } />
          <Route path="/help-videos" element={
            <ProtectedRoute>
              <HelpVideosPage />
            </ProtectedRoute>
          } />
          <Route path="/support" element={
            <ProtectedRoute>
              <SupportRequestsPage />
            </ProtectedRoute>
          } />
<Route path="/setup-wizard" element={
            <ProtectedRoute>
              <SelfServiceSetupWizard />
            </ProtectedRoute>
          } />
          <Route path="/add-hardware" element={
            <AdminRoute>
              <AddHardwarePage />
            </AdminRoute>
          } />
          <Route path="/add-location" element={
            <AdminRoute>
              <AddLocationPage />
            </AdminRoute>
          } />
          <Route path="/admin" element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          } />
          {/* NOTE: /admin/alerts route removed Dec 7, 2025 - orphaned feature */}
          <Route path="/admin/alert-management" element={
            <AdminRoute>
              <AlertManagementPage />
            </AdminRoute>
          } />
          <Route path="/admin/settings" element={
            <AdminRoute>
              <SystemSettingsPage />
            </AdminRoute>
          } />
          <Route path="/admin/billing" element={
            <ProtectedRoute>
              <BillingPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/integrations" element={
            <ProtectedRoute>
              <IntegrationsPage />
            </ProtectedRoute>
          } />
          {process.env.REACT_APP_OWNER_SHARING_ENABLED === 'true' && <Route path="/data-sharing" element={
            <ProtectedRoute>
              <OwnerSharingPage />
            </ProtectedRoute>
          } />}
          {/* Partner Access removed 2026-10-04; old bookmarks land on Admin */}
          <Route path="/admin/data-sharing" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/inventory-config" element={
            <AdminRoute>
              <InventoryConfigPage />
            </AdminRoute>
          } />
          <Route path="/admin/daily-revenue" element={
            <ManagerRoute>
              <DailyRevenuePage />
            </ManagerRoute>
          } />
          <Route path="/admin/activity-report" element={
            <AdminRoute>
              <ActivityReportPage />
            </AdminRoute>
          } />
          {/* Open to every role: anyone can record tip changes and titrations. */}
          <Route path="/admin/injector-tips" element={
            <ProtectedRoute>
              <InjectorTipsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/reference-library" element={
            <AdminRoute>
              <ReferenceLibraryPage />
            </AdminRoute>
          } />
          <Route path="/admin/checklists" element={
            <AdminRoute>
              <ChecklistsPage />
            </AdminRoute>
          } />
          <Route path="/admin/env-sensors" element={
            <AdminRoute>
              <EnvSensorsPage />
            </AdminRoute>
          } />
          <Route path="/admin/data-cleanup" element={
            <AdminRoute>
              <DataCleanupPage />
            </AdminRoute>
          } />
          <Route path="/admin/wash-package-counts" element={
            <AdminRoute>
              <WashPackageCountsPage />
            </AdminRoute>
          } />
          <Route path="/admin/average-volume" element={
            <ManagerRoute>
              <AverageVolumePage />
            </ManagerRoute>
          } />
          <Route path="/admin/raw-pulses" element={
            <AdminRoute>
              <RawPulsesPage />
            </AdminRoute>
          } />
          <Route path="/admin/wash-replay" element={
            <AdminRoute>
              <WashReplayPage />
            </AdminRoute>
          } />
          <Route path="/admin/estimated-dilution" element={
            <AdminRoute>
              <EstimatedDilutionPage />
            </AdminRoute>
          } />
          {/* Developer Console Routes */}
          <Route path="/developer/*" element={
            <DeveloperRoute>
              <DeveloperConsole />
            </DeveloperRoute>
          } />
          <Route path="/developer/users" element={
            <DeveloperRoute>
              <UserManagement />
            </DeveloperRoute>
          } />
          <Route path="/developer/access-matrix" element={
            <DeveloperRoute>
              <AccessMatrix />
            </DeveloperRoute>
          } />
          <Route path="/developer/firmware" element={
            <DeveloperRoute>
              <FirmwareManagement />
            </DeveloperRoute>
          } />
          <Route path="/developer/dencar" element={
            <DeveloperRoute>
              <DencarSubscribers />
            </DeveloperRoute>
          } />
          <Route path="/developer/catalogs" element={
            <DeveloperRoute>
              <CatalogManagementPage />
            </DeveloperRoute>
          } />
          <Route path="/developer/sds" element={
            <DeveloperRoute>
              <SdsLibraryPage />
            </DeveloperRoute>
          } />
        </Routes>
        </Suspense>
        </AppErrorBoundary>
      </div>
      <InstallAppPrompt enabled={Boolean(currentUser && !isPublicRoute)} />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ImpersonationProvider>
          <SystemSettingsProvider>
            <TenantProvider>
              <TenantThemeProvider>
                <LocationProvider>
                  <SupportProvider>
                    <LocationSettingsSync>
                      <SensorProvider>
                        <AppContent />
                        <UpdateToast />
                      </SensorProvider>
                    </LocationSettingsSync>
                  </SupportProvider>
                </LocationProvider>
              </TenantThemeProvider>
            </TenantProvider>
          </SystemSettingsProvider>
        </ImpersonationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
